import { create } from 'zustand';
import { supabase } from '@/utils/supabase';
import type { Order } from '@/utils/supabase';
import { getCacheBuster } from './refreshStore';
import { getOrdersAblyClient, getOrdersChannel } from '@/utils/ably';
import { useAssignmentStrategyStore, ensureAssignmentConfigLoaded } from '@/store/assignmentStrategyStore';
import { useAuthStore } from './authStore';

interface CreateOrderData {
    id: string;
    user_id: string | null;
    subtotal: number;
    delivery_fee: number;
    discount: number;
    voucher_id: string | null;
    total: number;
    payment_method: Order['payment_method'];
    payment_status: Order['payment_status'];
    status: Order['status'];
    delivery_address: Order['delivery_address'];
    contact_phone: string;
    notes?: string | null;
    order_items: Array<{
        product_id: string;
        quantity: number;
        price_at_time: number;
        total: number;
    }>;
    guest_email?: string | null;
    guest_name?: string | null;
    guest_phone?: string | null;
}

let myOrdersAbortController: AbortController | null = null;
let allOrdersAbortController: AbortController | null = null;
let staffOrdersAbortController: AbortController | null = null;

interface OrderState {
    orders: Order[];
    currentOrder: Order | null;
    isLoading: boolean;
    error: string | null;
    totalOrders: number;
    isSoundEnabled: boolean;
    unreadOrderCount: number;

    // Admin actions
    fetchAllOrders: (page?: number, limit?: number) => Promise<void>;

    // Customer actions
    fetchMyOrders: (userId: string, page?: number, limit?: number) => Promise<void>;
    fetchOrderById: (id: string) => Promise<Order | null>;
    createOrder: (orderData: CreateOrderData) => Promise<Order | null>;

    // Resilient checkout: local-only state update + side-effects (no DB call).
    // Called by Checkout.tsx AFTER useResilientOrder.submitOrder() succeeds.
    addCreatedOrder: (orderData: CreateOrderData, serverOrderId: string) => Order;
    
    // Staff actions
    fetchStaffOrders: (staffId: string, page?: number, limit?: number) => Promise<void>;
    checkExpiredOrders: () => Promise<void>;
    setSoundEnabled: (enabled: boolean) => void;
    incrementUnreadOrderCount: () => void;
    resetUnreadOrderCount: () => void;

    // Real-time Global Sync
    realtimeUpdateTick: number;
    newOrderTick: number;
    initRealtimeSubscription: () => void;
    cleanupRealtimeSubscription: () => void;
}

export const useOrderStore = create<OrderState>((set, get) => ({
    orders: [],
    currentOrder: null,
    isLoading: false,
    error: null,
    totalOrders: 0,
    realtimeUpdateTick: 0,
    newOrderTick: 0,
    isSoundEnabled: false,
    unreadOrderCount: 0,

    fetchAllOrders: async (page = 1, limit = 20) => {
        limit = Math.min(limit, 100); // M2 Fix: Enforce max limit
        if (allOrdersAbortController) {
            allOrdersAbortController.abort();
        }
        allOrdersAbortController = new AbortController();
        const { signal } = allOrdersAbortController;

        if (get().orders.length === 0) set({ isLoading: true });
        set({ error: null });


        try {
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            const fetchPromise = supabase
                .from('orders')
                .select('*, order_items(*, products(*)), profiles:user_id(full_name, email, phone), staff:profiles!assigned_staff_id(full_name)', { count: 'exact' })
                .neq('id', getCacheBuster())
                .order('created_at', { ascending: false })
                .range(from, to)
                .abortSignal(signal);

            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request Timeout')), 12000)
            );

            const { data, error, count } = await Promise.race([
                fetchPromise,
                timeoutPromise
            ]) as Awaited<typeof fetchPromise>;

            if (error) throw error;
            set({ orders: data || [], totalOrders: count || 0, isLoading: false });
        } catch (error: unknown) {
            if ((error instanceof DOMException && error.name === 'AbortError') || (error && typeof error === 'object' && 'message' in error && String(error.message).includes('AbortError'))) return;
            if (import.meta.env.DEV) console.error('[OrderStore] Fetch all orders error:', error);
            set({ error: 'An unexpected error occurred.', isLoading: false });
        } finally {
            allOrdersAbortController = null;
        }
    },

    fetchMyOrders: async (userId, page = 1, limit = 20) => {
        if (myOrdersAbortController) {
            myOrdersAbortController.abort();
        }
        myOrdersAbortController = new AbortController();
        const { signal } = myOrdersAbortController;

        if (get().orders.length === 0) set({ isLoading: true });
        set({ error: null });

        try {
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            const fetchPromise = supabase
                .from('orders')
                .select('*, order_items(*, products(*))', { count: 'exact' })
                .neq('id', getCacheBuster())
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .range(from, to)
                .abortSignal(signal);

            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request Timeout')), 12000)
            );

            const { data, error, count } = await Promise.race([
                fetchPromise,
                timeoutPromise
            ]) as Awaited<typeof fetchPromise>;

            if (error) throw error;
            set({ orders: data || [], totalOrders: count || 0, isLoading: false });
        } catch (error: unknown) {
            if ((error instanceof DOMException && error.name === 'AbortError') || (error && typeof error === 'object' && 'message' in error && String(error.message).includes('AbortError'))) return;
            if (import.meta.env.DEV) console.error('Error fetching orders:', error);
            set({ error: 'An unexpected error occurred.', isLoading: false });
        } finally {
            myOrdersAbortController = null;
        }
    },

    fetchStaffOrders: async (staffId, page = 1, limit = 20) => {
        if (staffOrdersAbortController) {
            staffOrdersAbortController.abort();
        }
        staffOrdersAbortController = new AbortController();
        const { signal } = staffOrdersAbortController;

        if (get().orders.length === 0) set({ isLoading: true });
        set({ error: null });

        try {
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            // [CLAIMING MODE] In claiming mode, fetch BOTH assigned orders
            // AND all unassigned pending orders so every staff sees them.
            // In round-robin mode, only fetch orders assigned to this staff.
            const { isClaimingMode } = useAssignmentStrategyStore.getState();

            let fetchPromise;
            if (isClaimingMode()) {
                // Fetch orders assigned to me OR unassigned pending orders
                fetchPromise = supabase
                    .from('orders')
                    .select(`
                        *,
                        profiles:user_id (*),
                        order_items (*, products (*))
                    `, { count: 'exact' })
                    .neq('id', getCacheBuster())
                    .or(`assigned_staff_id.eq.${staffId},and(assigned_staff_id.is.null,status.eq.pending)`)
                    .order('created_at', { ascending: false })
                    .range(from, to)
                    .abortSignal(signal);
            } else {
                fetchPromise = supabase
                    .from('orders')
                    .select(`
                        *,
                        profiles:user_id (*),
                        order_items (*, products (*))
                    `, { count: 'exact' })
                    .neq('id', getCacheBuster())
                    .eq('assigned_staff_id', staffId)
                    .order('created_at', { ascending: false })
                    .range(from, to)
                    .abortSignal(signal);
            }

            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request Timeout')), 12000)
            );

            const { data, error, count } = await Promise.race([
                fetchPromise,
                timeoutPromise
            ]) as Awaited<typeof fetchPromise>;

            if (error) throw error;
            set({ orders: data || [], totalOrders: count || 0, isLoading: false });
        } catch (error: unknown) {
            if ((error instanceof DOMException && error.name === 'AbortError') || (error && typeof error === 'object' && 'message' in error && String(error.message).includes('AbortError'))) return;
            if (import.meta.env.DEV) console.error('Error fetching staff orders:', error);
            set({ error: 'An unexpected error occurred.', isLoading: false });
        } finally {
            staffOrdersAbortController = null;
        }
    },

    fetchOrderById: async (id) => {
        set({ isLoading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('orders')
                .select('*, order_items(*, products(*)), profiles:user_id(full_name, email, phone), staff:profiles!assigned_staff_id(full_name)')
                .eq('id', id)
                .single();

            if (error) throw error;
            set({ currentOrder: data, isLoading: false });
            return data;
        } catch (error: unknown) {
            if (import.meta.env.DEV) console.error('Error fetching order:', error);
            set({ error: 'An unexpected error occurred.', isLoading: false });
            return null;
        }
    },

    createOrder: async (orderData) => {
        set({ isLoading: true, error: null });
        
        try {
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('TIMEOUT|Order placement timed out. Please check your connection and try again.')), 20000)
            );

            const rpcPromise = supabase.rpc('place_order_transaction', {
                p_user_id: orderData.user_id,
                p_subtotal: orderData.subtotal,
                p_delivery_fee: orderData.delivery_fee,
                p_discount: orderData.discount,
                p_voucher_id: orderData.voucher_id,
                p_total: orderData.total,
                p_payment_method: orderData.payment_method,
                p_payment_status: orderData.payment_status,
                p_status: orderData.status,
                p_delivery_address: orderData.delivery_address,
                p_contact_phone: orderData.contact_phone,
                p_notes: orderData.notes || null,
                p_order_items: orderData.order_items,
                p_guest_email: orderData.guest_email || null,
                p_guest_name: orderData.guest_name || null,
                p_guest_phone: orderData.guest_phone || null
            });

            const { data, error } = await Promise.race([rpcPromise, timeoutPromise]) as Awaited<typeof rpcPromise>;

            if (error) {
                const fullMsg = [error.message, error.details, error.hint].filter(Boolean).join(' | ');
                throw new Error(fullMsg || 'Unknown backend error');
            }

            const serverOrderId = data?.order_id || orderData.id;

            // [ABLY REALTIME] Publish new order event for admin/staff
            const ordersClient = await getOrdersAblyClient();
            if (ordersClient) {
                const channel = getOrdersChannel(ordersClient);
                channel.publish('new_order', {
                    ...orderData,
                    id: serverOrderId,
                    assigned_staff_id: null,
                    assigned_at: null,
                    orderId: serverOrderId,
                    userId: orderData.user_id,
                    timestamp: Date.now(),
                }).catch((err) => {
                    if (import.meta.env.DEV) console.warn('[Ably] Failed to publish new_order:', err);
                });
            }

            // [STRATEGY-AWARE ASSIGNMENT] Ensure DB config is loaded before deciding
            ensureAssignmentConfigLoaded();
            const { isRoundRobinMode, config } = useAssignmentStrategyStore.getState();
            if (isRoundRobinMode() && config.enableAutoAssign) {
                supabase.rpc('assign_order_to_staff', { p_order_id: orderData.id })
                    .then(({ error: assError }) => {
                        if (assError) console.error("[Staff Assignment Error]:", assError);
                    });
            }
            // In claiming mode, do NOT auto-assign — order stays unassigned
            // Staff will see it on their dashboard and can claim it

            const { orders } = get();
            const now = new Date().toISOString();
            const orderWithItems: Order = {
                ...orderData,
                id: serverOrderId,
                assigned_staff_id: null,
                assigned_at: null,
                created_at: now,
                updated_at: now,
                order_items: orderData.order_items.map(item => ({
                    id: '',
                    order_id: serverOrderId,
                    product_id: item.product_id,
                    quantity: item.quantity,
                    price_at_time: item.price_at_time,
                    total: item.total,
                    created_at: now,
                })),
            } as Order;
            
            set({ orders: [orderWithItems, ...orders], isLoading: false });
            return orderWithItems;
        } catch (error: unknown) {
            if (import.meta.env.DEV) console.error('[OrderStore] createOrder error:', error);
            set({ error: 'An unexpected error occurred.', isLoading: false });
            return null;
        }
    },

    // ── addCreatedOrder ─────────────────────────────────────────────────────
    // CRITICAL: This function does NOT call the database.
    // It is the "store half" of the Zero-Ping Resilience architecture:
    //
    //   useResilientOrder (hook)  -> handles the Supabase RPC (with retry)
    //   addCreatedOrder   (store) -> handles Zustand state + Ably + assignment
    //
    // This separation ensures we never accidentally call the RPC twice,
    // which would double-charge the user or create duplicate orders.
    // ──────────────────────────────────────────────────────────────────────────
    addCreatedOrder: (orderData, serverOrderId) => {
        // 1. Publish Ably real-time event for admin/staff dashboards
        getOrdersAblyClient().then((ordersClient) => {
            if (ordersClient) {
                const channel = getOrdersChannel(ordersClient);
                channel.publish('new_order', {
                    ...orderData,
                    id: serverOrderId,
                    assigned_staff_id: null,
                    assigned_at: null,
                    orderId: serverOrderId,
                    userId: orderData.user_id,
                    timestamp: Date.now(),
                }).catch((err) => {
                    if (import.meta.env.DEV) console.warn('[Ably] Failed to publish new_order:', err);
                });
            }
        });

        // 2. Strategy-aware staff assignment (fire-and-forget RPC)
        ensureAssignmentConfigLoaded();
        const { isRoundRobinMode, config } = useAssignmentStrategyStore.getState();
        if (isRoundRobinMode() && config.enableAutoAssign) {
            supabase.rpc('assign_order_to_staff', { p_order_id: serverOrderId })
                .then(({ error: assError }) => {
                    if (assError) console.error('[Staff Assignment Error]:', assError);
                });
        }
        // In claiming mode, order stays unassigned for staff to claim

        // 3. Build the optimistic Order object for the Zustand state
        const { orders } = get();
        const now = new Date().toISOString();
        const orderWithItems: Order = {
            ...orderData,
            id: serverOrderId,
            assigned_staff_id: null,
            assigned_at: null,
            created_at: now,
            updated_at: now,
            order_items: orderData.order_items.map(item => ({
                id: '',
                order_id: serverOrderId,
                product_id: item.product_id,
                quantity: item.quantity,
                price_at_time: item.price_at_time,
                total: item.total,
                created_at: now,
            })),
        } as Order;

        // 4. Update local Zustand state (optimistic)
        set({ orders: [orderWithItems, ...orders] });

        return orderWithItems;
    },

    checkExpiredOrders: async () => {
        try {
            await supabase.rpc('reassign_expired_orders');
        } catch (error) {
            if (import.meta.env.DEV) console.error('[OrderStore] checkExpiredOrders error:', error);
        }
    },



    setSoundEnabled: (enabled) => set({ isSoundEnabled: enabled }),

    incrementUnreadOrderCount: () => {
        set((state) => ({ unreadOrderCount: state.unreadOrderCount + 1, newOrderTick: state.newOrderTick + 1 }));
    },

    resetUnreadOrderCount: () => set({ unreadOrderCount: 0 }),

    initRealtimeSubscription: async () => {
        const channels = supabase.getChannels();
        const existingChannel = channels.find(c => c.topic.includes('global_orders'));

        // [INDUSTRIAL GRADE RE-SYNC]
        // Strictly kill any previous connection before starting a new one. 
        // This prevents the 'Multiple Channels' error that causes browsers to hang.
        if (existingChannel) {
            if (import.meta.env.DEV) console.log('[Realtime-Orders] ️ Resetting stale connection pool...');
            await supabase.removeChannel(existingChannel);
        }

        const postgresConfig = { event: '*' as const, schema: 'public', table: 'orders' };
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let retryCount = 0;
        const MAX_RETRIES = 5;

        const connect = () => {
            supabase.channel('global_orders')
                .on('postgres_changes', postgresConfig, (payload) => {
                    const authState = useAuthStore.getState();
                    const currentUserId = authState.user?.id;
                    const currentRole = authState.profile?.role;

                    const { eventType, new: newRecord, old: oldRecord } = payload;
                    const updatedOrder = newRecord as Partial<Order>;

                    // [RELIABILITY CHECK]
                    // If we receive an event while the dashboard is hibernating, 
                    // we immediately trigger a 'soft-sync' pulse.
                    if (eventType === 'INSERT') {
                        // Admin/Manager always see it. Staff see it if claiming is active (unassigned) or just general sync
                        if (currentRole === 'admin' || currentRole === 'manager' || currentRole === 'staff') {
                            set((state) => ({ realtimeUpdateTick: state.realtimeUpdateTick + 1 }));
                        }
                    } else if (eventType === 'UPDATE') {
                        const { orders, currentOrder, fetchOrderById } = get();
                        const existingOrder = orders.find(o => o.id === updatedOrder.id);
                        
                        if (existingOrder || currentOrder?.id === updatedOrder.id) {
                            if (existingOrder) {
                                const newOrders = orders.map(o => o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o);
                                set({ orders: newOrders as Order[] });
                            }

                            if (debounceTimer) clearTimeout(debounceTimer);
                            debounceTimer = setTimeout(async () => {
                                const hydratedOrder = await fetchOrderById(updatedOrder.id as string);
                                if (hydratedOrder) {
                                    set((state) => ({
                                        orders: state.orders.map(o => o.id === hydratedOrder.id ? hydratedOrder : o)
                                    }));
                                }
                            }, 1200);
                        } else if (currentRole === 'staff' && (updatedOrder.assigned_staff_id === currentUserId || !updatedOrder.assigned_staff_id)) {
                            if (debounceTimer) clearTimeout(debounceTimer);
                            debounceTimer = setTimeout(() => {
                                set((state) => ({ realtimeUpdateTick: state.realtimeUpdateTick + 1 }));
                            }, 2000);
                        }
                    } else if (eventType === 'DELETE') {
                        const { orders } = get();
                        set({ orders: orders.filter(o => o.id !== oldRecord?.id) });
                    }
                })
                .subscribe((status) => {
                    if (status === 'SUBSCRIBED') {
                        if (import.meta.env.DEV) console.log('%c[Realtime-Orders] 🟢 ACTIVE', 'color: #10b981; font-weight: bold;');
                        retryCount = 0;
                    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                        console.error(`[Realtime-Orders] 🔴 OFFLINE: ${status}`);
                        
                        if (retryCount < MAX_RETRIES) {
                            retryCount++;
                            const backoff = Math.min(1000 * Math.pow(2, retryCount), 10000);
                            setTimeout(() => connect(), backoff);
                        }
                    }
                });
        };

        connect();
    },

    cleanupRealtimeSubscription: () => {
        const channels = supabase.getChannels();
        const globalChannel = channels.find(c => c.topic.includes('global_orders'));
        if (globalChannel) supabase.removeChannel(globalChannel);
    }
}));
